import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { keycloak } from "..";
import {
  APP_API,
  AUTH_LOCALSTORAGE_NAME,
  HOLIDAY_PRODUCT_ID,
} from "../constants/constants";

const pickProductIdFromToken = (jwtToken?: string): string | undefined => {
  if (!jwtToken) return undefined;

  try {
    const tokenValue = jwtToken.replace(/^Bearer\s+/i, "");
    const parts = tokenValue.split(".");
    if (parts.length < 2) return undefined;

    const normalizedPayload = parts[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(parts[1].length / 4) * 4, "=");
    const payload = JSON.parse(atob(normalizedPayload));

    const candidates = [
      payload?.productid,
      payload?.productId,
      payload?.product_id,
      payload?.userProject?.id,
      payload?.userProject?.productid,
      payload?.userProject?.productId,
      payload?.userProject?.product_id,
      payload?.user_project?.id,
      payload?.userProjects?.[0]?.id,
      payload?.user_projects?.[0]?.id,
      payload?.projects?.[0]?.id,
    ];

    const picked = candidates.find(
      (item) => item !== undefined && item !== null && String(item).trim()
    );

    return picked ? String(picked).trim() : undefined;
  } catch (error) {
    return undefined;
  }
};

const normalizeProductId = (productId?: string | null): string | undefined => {
  const normalized = String(productId || "").trim();
  return normalized && normalized !== "0" ? normalized : undefined;
};

export const appServiceApi = createApi({
  reducerPath: "appServiceApi",
  baseQuery: async (args, api, extraOptions) => {
    const baseQuery = fetchBaseQuery({
      baseUrl: APP_API,
      prepareHeaders: async (headers, { getState }) => {
        const authState = (getState() as any)?.auth;
        const authStoreToken = authState?.access_token;
        let token =
          typeof authStoreToken === "string"
            ? authStoreToken
            : authStoreToken?.access_token || authStoreToken?.token;

        if (!token) {
          const savedAuth = localStorage.getItem(AUTH_LOCALSTORAGE_NAME);
          if (savedAuth) {
            try {
              const parsedAuth = JSON.parse(savedAuth);
              token =
                parsedAuth?.access_token ||
                parsedAuth?.token ||
                parsedAuth?.user?.data?.token;
            } catch (error) {
              token = undefined;
            }
          }
        }

        if (typeof token === "string" && token.startsWith("Bearer ")) {
          token = token.replace(/^Bearer\s+/i, "");
        }
        if (!token && keycloak?.token) {
          token = keycloak.token;
        }

        if (token) {
          headers.set("Authorization", `Bearer ${token}`);
        }

        const productId =
          normalizeProductId(localStorage.getItem("product_id")) ||
          normalizeProductId(pickProductIdFromToken(token)) ||
          normalizeProductId(HOLIDAY_PRODUCT_ID);

        if (productId) {
          headers.set("productid", productId);
          headers.set("productId", productId);
          headers.set("x-productid", productId);
        }

        headers.set("ngrok-skip-browser-warning", "true");

        return headers;
      },
    });

    const result = await baseQuery(args, api, extraOptions);

    // if (
    //   result.error &&
    //   result.error?.status === 401 &&
    //   (result?.error?.data as any)?.message === "Unauthorized"
    // ) {
    //   // localStorage.removeItem(AUTH_LOCALSTORAGE_NAME);
    //   window.location.reload();
    // }

    //  result.error?.status === 401 &&
    // (result?.error?.data as any)?.message === "Authorization token missing"
    if (
      result.error &&
      result.error?.status === 401 &&
      (result?.error?.data as any)?.message ===
        "OTP verification failed: OTP Expired"
    ) {
      return result;
    }

    const errorData = (result?.error?.data as any) || {};
    const errorCode =
      errorData?.code ||
      errorData?.data?.code ||
      (typeof errorData?.message === "object" ? errorData?.message?.code : "");
    const isDeactivated = errorCode === "ACCOUNT_DEACTIVATED";

    if (result.error && result.error?.status === 401 && isDeactivated) {
      if (window.location.pathname !== "/admin/account-deactivated") {
        window.location.assign("/admin/account-deactivated");
      }
      return result;
    }

    if (
      result.error &&
      result.error?.status === 401
      // (result?.error?.data as any)?.message === "Authorization token missing"
    ) {
      try {
        await keycloak.logout();
        localStorage.clear();
      } catch (err) {
        console.error("Keycloak logout failed:", err);
      }
    }

    return result;
  },
  tagTypes: [
    "GetAllCompany",
    "GetSingleCompany",
    "GetUser",
    "GetUsers",
    "GetSingleUser",
    "GetAllVendorBatch",
    "GetVendorPayment",
    "GetDisbursmentPayment",
    "GetBalanceCheck",
    "GetAllDisbursementBatch",
    "GetVendorAmount",
    "GetSalaryAmount",
    "CheckVendorDisbursement",
    "CheckSalaryDisbursement",
    "GetAllProduct",
    "UserProduct",
    "GetAllRole",
    "GetUsersByProjectRole",
    "GetProjectsByUser",
    "GetAllWorkFlow",
    "GetAllBatch",
    "GetSingleBatch",
    "GetBatchLog",
    "GetUserPerformBatch",
    "GetUserPoolProject",
    "GetSingleWorkFlow",
    "GetAllBatchVendor",
    "GetAllVendorsPayment",
    "GetAllBatchPendingList",
    "GetDashboardData",
    "CheckUserBelongProject",
    "GetUserLogData",
    "UserCompanyCount",
    "GetAllStatements",
    "GetDownloadStatements",
    "GetBankStatementsDownload",
    "GetAllAssignProject",
    "GetAllProtalDepartment",
    "GetSingleProtalDepartment",
    "GetAllProtalSubDepartment",
    "GetSingleProtalSubDepartment",
    "GetPortalSingleSubDepartmentById",
    "GetUserStatus",
    "OtpRequestTime",
    "GetAllDisbursementType",
    "GetDepartments",
    "GetDepartmentDetails",
    "GetDesignations",
    "GetDesignationDetails",
    "GetWorkStations",
    "GetWorkStationDetails",
    "GetExpenseUnits",
    "GetExpenseUnitDetails",
    "GetEmployees",
    "GetEmployeeDetails",
    "GetEmployeeAdditionalFields",
    "GetEmployeeAdditionalFieldDetails",
    "GetBusinessData",
    "GetGrade",
    "GetEmployeeBreakdownSalaryDetails",
    "GetAdditionDeductionSalary",
    "GetVariableSalary",
    "GetReport",
    "GetPayrollSetting",
    "GetPayComponentSetting",
    "GetApprovalMatrix",
    "GetApprovalMatrixDetails",
    "GetRoles",
    "GetUserList",
    "GetCompanyWallet",
    "GetAllWalletTag",
    "GetSingleWalletTag",
    "GetAllWallet",
    "GetSingleWallet",
    "GetUserWalletRoleById",
    "GetUserWalletRoleByUserId",
    "GetUsersWithDisbursementAccess",
    "GetUserPoolByWalletId",
    "GetSingleUserWalletRole",
    "GetAllPermission",
    "GetPermissionDetails",
    "GetAllUserPermissionId",
    "GetAllGroupByUserId",
    "GetLogByUserId",
  ],
  keepUnusedDataFor: 0,

  endpoints: () => ({}),
});
